/**
 * The brand side of the sign-in and lock screens: always "charcoal night",
 * the logo, a calm promise and an abstract multi-office schedule (no data,
 * nothing about patients before sign-in). A brand moment (BUILD-PLAN 3.1):
 * the gradient lives here, not on the form.
 */
import { PRODUCT } from '@velora/contracts';
import { BrandMark } from '@velora/ui';
import { Building2, Eye, ShieldCheck } from 'lucide-react';
import type { CSSProperties } from 'react';

interface Block {
  top: number;
  height: number;
  tone: number;
}

/** Abstract appointment blocks per operatory column (percent of the day). */
const COLUMNS: ReadonlyArray<ReadonlyArray<Block>> = [
  [
    { top: 2, height: 24, tone: 1 },
    { top: 30, height: 36, tone: 1 },
    { top: 72, height: 20, tone: 4 },
  ],
  [
    { top: 8, height: 30, tone: 2 },
    { top: 44, height: 22, tone: 2 },
    { top: 70, height: 26, tone: 5 },
  ],
  [
    { top: 2, height: 42, tone: 3 },
    { top: 50, height: 22, tone: 6 },
    { top: 78, height: 16, tone: 3 },
  ],
  [
    { top: 14, height: 22, tone: 4 },
    { top: 40, height: 38, tone: 3 },
  ],
];

/** Hour ticks in the time gutter (marks only, no text). */
const HOURS = ['8', '10', '12', '2', '4'];

/** Abstract daily columns for the floating "all offices" card (two offices stacked). */
const BARS: ReadonlyArray<readonly [number, number]> = [
  [38, 22],
  [52, 26],
  [44, 30],
  [61, 24],
  [48, 34],
  [70, 30],
  [57, 38],
];

export function BrandHero() {
  return (
    <section className="idn-hero" data-theme="dark" aria-label={PRODUCT.name}>
      <span className="idn-hero__seam" aria-hidden />
      <div className="idn-hero__top">
        <BrandMark name={PRODUCT.shortName} size={36} wordmark className="idn-hero__logo" />
      </div>

      <div className="idn-hero__stage" aria-hidden>
        <div className="idn-hero__preview">
          <div className="idn-hero__bar">
            <span className="idn-hero__pill">
              <span
                className="idn-hero__dot"
                style={{ '--dot': 'var(--chart-1)' } as CSSProperties}
              />
              <span
                className="idn-hero__dot"
                style={{ '--dot': 'var(--chart-2)' } as CSSProperties}
              />
              <span
                className="idn-hero__dot"
                style={{ '--dot': 'var(--chart-3)' } as CSSProperties}
              />
              <span className="idn-hero__pill-text" />
            </span>
            <span className="idn-hero__line" />
            <span className="idn-hero__chip" />
          </div>
          <div className="idn-hero__heads">
            <span />
            {COLUMNS.map((_, i) => (
              <span key={i} className="idn-hero__head">
                <span className="idn-hero__avatar" />
                <span className="idn-hero__head-text" />
              </span>
            ))}
          </div>
          <div className="idn-hero__body">
            <div className="idn-hero__hours">
              {HOURS.map((h) => (
                <span key={h} />
              ))}
            </div>
            <div className="idn-hero__grid">
              {COLUMNS.map((col, i) => (
                <div key={i} className="idn-hero__col">
                  {col.map((b, j) => (
                    <span
                      key={j}
                      className="idn-hero__appt"
                      style={
                        {
                          top: `${b.top}%`,
                          height: `${b.height}%`,
                          '--tone': `var(--chart-${b.tone})`,
                        } as CSSProperties
                      }
                    >
                      <span className="idn-hero__appt-t" />
                      {b.height > 20 ? <span className="idn-hero__appt-m" /> : null}
                    </span>
                  ))}
                </div>
              ))}
              <span className="idn-hero__now" />
            </div>
          </div>
        </div>

        <div className="idn-hero__float">
          <span className="idn-hero__float-label" />
          <span className="idn-hero__float-value" />
          <span className="idn-hero__bars">
            {BARS.map(([a, b], i) => (
              <span key={i} className="idn-hero__col-bar" style={{ height: `${a + b}%` }}>
                <span
                  className="idn-hero__seg"
                  style={{ flexGrow: b, '--tone': 'var(--chart-2)' } as CSSProperties}
                />
                <span
                  className="idn-hero__seg"
                  style={{ flexGrow: a, '--tone': 'var(--chart-1)' } as CSSProperties}
                />
              </span>
            ))}
          </span>
        </div>
      </div>

      <div className="idn-hero__copy">
        <p className="idn-hero__eyebrow">Practice OS for dental groups</p>
        <h1 className="idn-hero__title">
          Every chair, every office,
          <br />
          <span className="idn-hero__gradient">one calm screen.</span>
        </h1>
        <p className="idn-hero__sub">
          Schedule, chart, bill and talk to patients across every location, one office at a time or
          all of them together.
        </p>
        <ul className="idn-hero__trust">
          <li>
            <ShieldCheck size={15} aria-hidden />
            Two-step sign-in for owners and managers
          </li>
          <li>
            <Building2 size={15} aria-hidden />
            Each office sees only what it should
          </li>
          <li>
            <Eye size={15} aria-hidden />
            Every chart view is audited
          </li>
        </ul>
      </div>
    </section>
  );
}
